import { Component, lazy, Suspense } from 'react';
import { ASSETS } from '../config/site';
import { useLocale } from '../context/LocaleContext';
import { FAQSection, FinalCTA } from '../components/shared/PageSections';
import { SparkIcon } from '../components/ui/Icons';
import { ButtonLink, Eyebrow, SectionIntro } from '../components/ui/Primitives';

const Lanyard = lazy(() => import('../components/reactbits/Lanyard/Lanyard'));

function CvVisualFallback() {
  return <div className="resource-lanyard-fallback" aria-hidden="true"><img src={ASSETS.exampleCv} alt="" /></div>;
}

class VisualErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function canRenderInteractiveLanyard() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData) return false;
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

function ResourceHeroVisual({ resourceKey, locale }) {
  if (resourceKey === 'cv') {
    if (!canRenderInteractiveLanyard()) return <CvVisualFallback />;
    return (
      <VisualErrorBoundary fallback={<CvVisualFallback />}>
        <Suspense fallback={<div className="visual-loader"><span /><span /><span /></div>}>
          <div className="resource-lanyard">
            <Lanyard
              position={[0, 0, 24]}
              gravity={[0, -38, 0]}
              fov={22}
              transparent
              frontImage={`/assets/lanyard/card-front-${locale}.svg`}
              backImage="/assets/lanyard/card-back.svg"
              imageFit="cover"
              lanyardWidth={0.95}
            />
          </div>
        </Suspense>
      </VisualErrorBoundary>
    );
  }

  if (resourceKey === 'motivationLetter') {
    return (
      <div className="resource-paper-stack" aria-hidden="true">
        <img src={ASSETS.templateCv} alt="" />
        <span className="resource-paper-stack__pen" />
        <span className="resource-paper-stack__spark"><SparkIcon /></span>
      </div>
    );
  }

  if (resourceKey === 'careerCheck') {
    return (
      <div className="career-orbit" aria-hidden="true">
        <span><SparkIcon /></span>
        <i>01</i><i>02</i><i>03</i>
        <img src={ASSETS.comicGirl} alt="" />
      </div>
    );
  }

  if (resourceKey === 'apprenticeships') {
    return (
      <div className="jobs-phone" aria-hidden="true">
        <img src={ASSETS.phone} alt="" />
        <div className="jobs-phone__card"><strong>96%</strong><span>YourApplicant Match</span></div>
      </div>
    );
  }

  return (
    <div className="coach-resource-visual" aria-hidden="true">
      <img src="/assets/original/coach-blue-big-orb.png" alt="" />
      <img src="/assets/original/coach-red-orb.png" alt="" />
      <span><SparkIcon /></span>
    </div>
  );
}

function ResourceHero({ content, resourceKey, locale }) {
  return (
    <section className={`resource-hero resource-hero--${resourceKey}`} aria-labelledby="resource-title">
      <div className="resource-hero__copy">
        <Eyebrow>{content.eyebrow}</Eyebrow>
        <h1 id="resource-title">{content.title} <span>{content.titleAccent}</span></h1>
        <p>{content.subtitle}</p>
        <ButtonLink>{content.cta}</ButtonLink>
      </div>
      <div className="resource-hero__visual"><ResourceHeroVisual resourceKey={resourceKey} locale={locale} /></div>
    </section>
  );
}

export default function ResourcePage({ resourceKey }) {
  const { t, locale } = useLocale();
  const content = t.resources[resourceKey];

  return (
    <main id="main-content" className="resource-page">
      <ResourceHero content={content.hero} resourceKey={resourceKey} locale={locale} />

      <section className="resource-intro section-shell" data-reveal="words">
        <p>{content.intro}</p>
      </section>

      <section className="resource-guidance section-shell">
        <SectionIntro eyebrow={content.hero.eyebrow} title={content.section.title} />
        <div className="resource-guidance__grid" data-reveal="stagger">
          {content.section.items.map((item, index) => (
            <article key={item.title} style={{ '--stagger': index }}>
              <span>0{index + 1}</span>
              <div className="resource-guidance__icon" aria-hidden="true">{index === 0 ? '↗' : index === 1 ? '✦' : '✓'}</div>
              <h3>{item.title}</h3><p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <FinalCTA eyebrow={content.hero.eyebrow} title={content.cta.title} text={content.cta.subtitle} buttonLabel={content.cta.label} />
      <FAQSection eyebrow={t.home.faq.eyebrow} title={content.faq.title} items={content.faq.items} />
    </main>
  );
}
